/**
 * @typedef NoticeItem - DB 로 관리 되는
 * @property {string} BOARD_SEQ - PK
 * @property {string} BOARD_CD - 게시물 구분 코드
 * @property {string} TITLE - 제목
 * @property {string} BODY - 내용
 * @property {string} REG_ID - 작성자 ID
 * @property {string} REG_NM - 작성자 이름
 * @property {string} REG_DT - 작성 일시
 * @property {string} MOD_ID - 수정자 ID
 * @property {string} MOD_DT - 수정 일시
 */
/**
 * @typedef {NoticeItem & {
 *   left: number,
 *   top: number
 * }} NoticeViewItem
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './NoticeContainer.module.css';
import { getLocalStorageItem, setLocalStorageItem } from '@maxis/client-logic/utils/localStorageUtil';
import { LOCAL_STORAGE_KEYS } from '@maxis/client-logic/constants/enumerates';
import { Button } from '@maxis/components/components';
import { useFetch } from '@maxis/client-logic/index';
import { sanitizeHtml } from '@maxis/client-logic/utils/sanitizeHtml';

const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 600;
const OFFSET_LEFT = 100;
const OFFSET_TOP = 100;

export default function NoticeContainer() {
    const { request } = useFetch();

    /**
     * @type {[NoticeViewItem[], Function]}
     */
    const [notices, setNotices] = useState([]);

    /** 드래그 상태 */
    const draggingRef = useRef(null);
    const offsetRef = useRef({ x: 0, y: 0 });

    const pageFunctions = {
        init: async () => {
            const response = await pageFunctions.search();
            if (response) {
                const excludeList = JSON.parse(
                    getLocalStorageItem(LOCAL_STORAGE_KEYS.NOTICE_EXCLUDE_LIST) ?? '[]'
                );

                const today = new Date().toISOString().slice(0, 10);

                const excludeSet = new Set(
                    excludeList.reduce((prev, current) => {
                        return current.HIDE_DT === today ? [...prev, current.BOARD_SEQ] : prev;
                    }, [])
                );

                setNotices(
                    response
                        .filter((item) => !excludeSet.has(item.BOARD_SEQ))
                        .map((item, index) => ({
                            ...item,
                            left: OFFSET_LEFT * index + OFFSET_LEFT,
                            top: OFFSET_TOP * index + OFFSET_TOP,
                        }))
                );
            }
        },
        search: () => {
            return request('/board/noticePopup', {
                method: 'POST',
                body: {},
            });
        },
        close: (BOARD_SEQ, exclude = false) => {
            setNotices((prev) => prev.filter((item) => item.BOARD_SEQ !== BOARD_SEQ));
            if (exclude) {
                const data = JSON.parse(
                    getLocalStorageItem(LOCAL_STORAGE_KEYS.NOTICE_EXCLUDE_LIST) ?? '[]'
                );

                const today = new Date().toISOString().slice(0, 10);
                data.push({ BOARD_SEQ: BOARD_SEQ, HIDE_DT: today });

                setLocalStorageItem(LOCAL_STORAGE_KEYS.NOTICE_EXCLUDE_LIST, JSON.stringify(data));
            }
        },
    };

    /** 드래그 시작 */
    const handleMouseDown = (e, BOARD_SEQ) => {
        draggingRef.current = BOARD_SEQ;

        const target = notices.find((n) => n.BOARD_SEQ === BOARD_SEQ);
        if (target) {
            offsetRef.current = {
                x: e.clientX - target.left,
                y: e.clientY - target.top,
            };
        }
    };

    /** 드래그 이동 */
    const handleMouseMove = useCallback((e) => {
        if (!draggingRef.current) return;

        setNotices((prev) =>
            prev.map((item) =>
                item.BOARD_SEQ === draggingRef.current
                    ? {
                          ...item,
                          left: e.clientX - offsetRef.current.x,
                          top: e.clientY - offsetRef.current.y,
                      }
                    : item
            )
        );
    }, []);

    /** 드래그 종료 */
    const handleMouseUp = useCallback(() => {
        draggingRef.current = null;
    }, []);

    useEffect(() => {
        pageFunctions.init();
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    return notices.map((item) => {
        return (
            <div
                key={item.BOARD_SEQ}
                className={styles.container}
                style={{
                    minWidth: DEFAULT_WIDTH,
                    minHeight: DEFAULT_HEIGHT,
                    left: item.left,
                    top: item.top,
                }}
            >
                <div className={styles.top} onMouseDown={(e) => handleMouseDown(e, item.BOARD_SEQ)}>
                    <span className={styles.subject}>{item.TITLE}</span>
                </div>

                <div className={styles.content}>
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.BODY) }} />
                </div>

                <div className={styles.footer}>
                    <Button
                        label={'오늘 하루 보지 않기'}
                        iconType={'btn_close'}
                        onClick={() => pageFunctions.close(item.BOARD_SEQ, true)}
                        checkAuth={false}
                    />
                    <Button
                        label={'닫기'}
                        colorType={'sub3'}
                        iconType={'btn_close'}
                        onClick={() => pageFunctions.close(item.BOARD_SEQ)}
                        checkAuth={false}
                    />
                </div>
            </div>
        );
    });
}
